import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setChatOpen, setPalette, setSidebarOpen, toggleTheme, setDrawerOpen,
} from '../../state/uiSlice';
import Icon3D from '../Icons3D';
import JobsIndicator from '../jobs/JobsIndicator';
import { PALETTES } from '../../config/appearance';
import { PROFILES, ROUTES, isHomePath } from '../../config/navigation';
import { useEmployeeSeat, useProfile } from '../../api/hooks';
import { useCopilot } from '../../api/copilot';
import { focusAgentHome } from '../copilot/AgentHome';
import { dateLong } from '../../lib/format';

/** How the button's title and label name each live state of the assistant. */
const AGENT_STATE_WORD = {
  confirm: 'needs your OK',
  listening: 'listening',
  thinking: 'working on it',
  speaking: 'speaking',
};

/**
 * The assistant's live state, drawn on the closed button.
 *
 * With the panel shut the top bar is the one place left to show that the mic
 * is open, an answer is being worked out, or the agent is talking — Ki_UI's
 * handoff of the wait from its overlay to the chat header, here. Pulse, dots
 * and bars are the same marks the panel itself uses for the same states, so
 * they read the same in both places.
 *
 * A confirmation card waiting for Run outranks all three: it is the one state
 * in which nothing happens until the reader acts, and with the panel shut it
 * would otherwise expire unseen.
 */
function AgentBadge({ state }) {
  if (state === 'confirm') {
    return <span className="copilot-badge confirm" aria-hidden="true">!</span>;
  }
  if (state === 'speaking') {
    return <span className="copilot-badge speaking" aria-hidden="true"><span className="eq"><i /><i /><i /><i /></span></span>;
  }
  if (state === 'thinking') {
    return <span className="copilot-badge thinking" aria-hidden="true"><span className="dots"><i /><i /><i /></span></span>;
  }
  return <span className="copilot-badge listening" aria-hidden="true" />;
}

/**
 * Greeting, palette, theme, notifications — the design's <header class="top">.
 *
 * The left slot used to be a search box that nothing listened to: `onSearch`
 * was never passed by AppLayout, so Enter did nothing and ⌘K was decoration.
 * It is a greeting and today's date instead — the one thing that box can say
 * truthfully.
 *
 * There is no "As of" picker here any more: it duplicated the "Data as of"
 * dates the screens already print beside their figures, and a second, editable
 * copy in the header could quietly re-date every analytics call.
 */
export default function Topbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  /* app_admin kept two things in its top right that belong to nobody's screen:
     the support console and the configuration cog. They live here for the same
     reason — a support call arrives while you are on some other screen, and
     configuration is reached from wherever you noticed it was wrong. Staff
     only; every other seat sees the bar it has always had. */
  const staff = useEmployeeSeat();
  const { palette, theme, chatOpen, role } = useSelector((s) => s.ui);
  /* On a seat's landing screen the assistant is in the page, so the column is
     never open there and the button puts the reader in that field instead. */
  const home = isHomePath(pathname);
  const dockOpen = chatOpen && !home;
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);
  const { user } = useProfile();
  /* The signed-in account's own name; the seat's persona only in the
     test-profile mode, where nobody is signed in to greet. Keyed on the session
     rather than on `user`, so a real account never flashes the persona's name
     while its profile is still loading. */
  const fullName = (isLoggedIn ? user?.name : PROFILES[role]?.name) || '';
  const firstName = fullName.trim().split(/\s+/)[0] || '';
  const {
    listening = false, thinking = false, speaking = false, awaitingConfirm = false,
  } = useCopilot();
  /* Only while the panel is closed: open, the panel says all of this itself,
     and a badge on the button that closes it would be a second copy. A card
     waiting for Run comes first; otherwise the most recent state in a turn
     wins — speaking over thinking over listening. */
  let agentState = null;
  if (!dockOpen) {
    if (awaitingConfirm) agentState = 'confirm';
    else if (speaking) agentState = 'speaking';
    else if (thinking) agentState = 'thinking';
    else if (listening) agentState = 'listening';
  }
  const copilotTitle = dockOpen
    ? 'Close KeepInvesting assistant'
    : agentState
      ? `Ask KeepInvesting — ${AGENT_STATE_WORD[agentState]}`
      : 'Ask KeepInvesting';
  /* The accessible name stays the same whether open or closed: it is a toggle,
     and `aria-pressed` already says which way it is set. Only the live state
     is added to it. */
  const copilotLabel = `KeepInvesting assistant${agentState ? ` — ${AGENT_STATE_WORD[agentState]}` : ''}`;

  return (
    <header className="top">
      <button className="icon-btn menu-btn" title="Menu" aria-label="Open menu" onClick={() => dispatch(setSidebarOpen(true))}>☰</button>

      {/* Reuses the search box's frame so the header keeps its shape and its
          responsive rules (full-width row on phones) without new CSS. */}
      <div className="search" style={{ color: 'var(--ink)', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13.5, fontWeight: 650 }}>
          Hello{firstName ? `, ${firstName}` : ''}
        </b>
        <span style={{ color: 'var(--line)' }}>|</span>
        <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>
          Today · <span style={{ color: 'var(--ink2)', fontFamily: "'Space Grotesk',sans-serif" }}>{dateLong(new Date())}</span>
        </span>
      </div>

      <div className="top-actions">
        {/* Only while an analysis is running — nothing otherwise. */}
        <JobsIndicator />

        {staff.active && (
          <>
            <button
              className={['icon-btn', pathname.startsWith(ROUTES.support) && 'on'].filter(Boolean).join(' ')}
              title="Support console — take customer calls and chats"
              aria-label="Support console"
              aria-pressed={pathname.startsWith(ROUTES.support)}
              onClick={() => navigate(pathname.startsWith(ROUTES.support) ? ROUTES.dashboard : ROUTES.support)}
            >
              <Icon3D name="call" size={15} />
            </button>
            <button
              className={['icon-btn', pathname.startsWith(ROUTES.config) && 'on'].filter(Boolean).join(' ')}
              title="Configuration — system settings"
              aria-label="Configuration"
              aria-pressed={pathname.startsWith(ROUTES.config)}
              onClick={() => navigate(ROUTES.config)}
            >
              <Icon3D name="settings" size={15} />
            </button>
          </>
        )}

        {/* Palette lives in Settings too, so it is the first thing to go. */}
        <select
          className="pal-sel palette-select"
          title="Color palette"
          value={palette}
          onChange={(e) => dispatch(setPalette(e.target.value))}
        >
          {PALETTES.map(([v, label]) => (
            <option key={v} value={v}>● {label}</option>
          ))}
        </select>

        <button className="icon-btn" title="Toggle dark / light" onClick={() => dispatch(toggleTheme())}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>

        <button className="icon-btn" title="Alerts" onClick={() => dispatch(setDrawerOpen(true))}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.7 21a2 2 0 01-3.4 0" />
          </svg>
          <span className="dot" />
        </button>

        {/* The assistant's only way in and out: the panel has no resting bar of
            its own, so this is what opens the column and what closes it.
            `aria-pressed` because it is a toggle, not an action. */}
        <button
          className={['icon-btn', 'copilot-btn', dockOpen && 'on', agentState].filter(Boolean).join(' ')}
          title={copilotTitle}
          aria-label={copilotLabel}
          aria-pressed={dockOpen}
          onClick={() => {
            if (home && focusAgentHome()) return;
            dispatch(setChatOpen(!chatOpen));
          }}
        >
          <Icon3D name="spark" size={15} />
          {agentState && <AgentBadge state={agentState} />}
        </button>
      </div>
    </header>
  );
}
